import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  useAdminCreateItem,
  useAdminUpdateItem,
  useGetItemTypesAll,
  useGetRarityTypesAll,
  useGetItemById,
  useAdminGetAllElements,
} from '@outdoria/api';
import { addCdnUrl, Button, IconType, Loader, Modal } from '@outdoria/ui';
import type { Element, Item } from '@outdoria/types';
import { ActionBar } from '../../containers';
import { Form } from '../../components';
import { useForm } from '../../hooks';
import { FormResource, FormCollectible } from './ItemTypeSpecificForm';

import s from './ItemUpsertView.module.scss';

/**
 * Create a new item.
 */
export const ItemCreateView = () => {
  const { item_type_id } = useParams();
  const { data, mutate } = useCreate();

  const defaultItem = {
    item_type_id: item_type_id as Item['item_type_id'],
    rarity_id: 'common' as Item['rarity_id'],
    description: '',
    image: '/items/',
    image_eq: '/items/',
    is_rotable: true,
    name: '',
    shape: '',
    stack_size: 1,
    thumbnail: '/items/',
    weight: 0,
  };

  const defaultElements: Element['element_id'][] = [];

  return (
    <ItemUpsertView
      defaultItem={defaultItem}
      defaultElements={defaultElements}
      actionLabel="Save new item"
      itemId={data?.item_id ?? null}
      mutate={mutate}
    />
  );
};

/**
 * Edit the selected item.
 */
export const ItemEditView = () => {
  const { item_id = 'UNKNOWN' } = useParams<Item['item_id']>();
  const { data, isLoading, isError } = useGetItemById(item_id);

  const { mutate } = useUpdate(item_id);

  if (isLoading) return <Loader />;

  if (isError) return <div>I broke :c</div>;

  return (
    <ItemUpsertView
      defaultItem={data.item}
      defaultElements={data.elements}
      actionLabel="Save updated item"
      itemId={item_id}
      mutate={mutate}
    />
  );
};

/**
 * Reused part
 */
type ItemUpsertViewProps = {
  defaultItem: Omit<Item, 'item_id'>;
  defaultElements: Element['element_id'][];
  actionLabel: string;
  itemId: string | null;
  mutate: (form: {
    item: Omit<Item, 'item_id'>;
    elements: Element['element_id'][];
  }) => void;
};
const ItemUpsertView = ({
  defaultItem,
  defaultElements,
  actionLabel,
  itemId,
  mutate,
}: ItemUpsertViewProps) => {
  const { item_type_id } = defaultItem;

  const shouldSave = useRef(false);
  const shouldReset = useRef(false);

  const { register, form, onReset } =
    useForm<Omit<Item, 'item_id'>>(defaultItem);

  const { rarityOptions, itemTypesOptions, elementsOptions, itemIcon } =
    useOptions(item_type_id);

  const [elements, setElements] =
    useState<Element['element_id'][]>(defaultElements);

  const isCollectible = item_type_id === 'collectible';

  const onItemMutate = () => {
    mutate({ item: form, elements });
    shouldSave.current = true;
    // TODO find a less hacky way because this honestly fucking sucks
    // Quickly change back to "should not mutate" after child saved.
    setTimeout(() => {
      shouldSave.current = false;
    }, 1000);
  };

  const onItemReset = () => {
    onReset();
    setElements(defaultElements);
    shouldReset.current = true;
    // TODO find a less hacky way because this honestly fucking sucks
    // Quickly change back to "should not reset" after child resetted.
    setTimeout(() => {
      shouldReset.current = false;
    }, 500);
  };

  const getItemSpecificForm = () => {
    if (item_type_id === 'resource') {
      // @ts-expect-error TODO I can't be bothered with those types
      return <FormResource register={register} form={form} />;
    }
    if (item_type_id === 'collectible') {
      return (
        <FormCollectible
          itemId={itemId}
          shouldSave={shouldSave.current}
          shouldReset={shouldReset.current}
        />
      );
    }
    return null;
  };

  return (
    <div className={s.view}>
      <ActionBar
        title={`Item type: ${item_type_id?.toUpperCase()}`}
        buttons={
          <>
            <Button label="Reset form" onClick={onItemReset} />
            <Button
              label={actionLabel}
              variant={Button.Variant.ACTION}
              onClick={onItemMutate}
            />
          </>
        }
      />

      <div className={s.view__content}>
        <div className={s.view__row}>
          <Form title="Basic item properties" icon="Sword" pretty>
            <Form.Select
              label="Item type"
              disabled
              data={itemTypesOptions}
              {...register('item_type_id', { required: true })}
            />

            <Form.Input
              label="Name"
              {...register('name', { required: true })}
            />

            <Form.Input
              label="Description"
              {...register('description', { required: true })}
            />

            <Form.Select
              label="Rarity"
              data={rarityOptions}
              {...register('rarity_id', { required: true })}
            />

            <Form.Input
              label="Weight"
              type="number"
              min={0}
              step={0.01}
              {...register('weight', { required: true })}
            />

            {!isCollectible && (
              <Form.Input
                label="Stack size"
                type="number"
                min={1}
                {...register('stack_size', { required: true })}
              />
            )}

            {!isCollectible && (
              <Form.CheckboxGroup
                label="Elements"
                name="elements"
                setValue={setElements}
                value={elements}
                data={elementsOptions}
              />
            )}
          </Form>
        </div>

        <div className={s.view__row}>
          <Form
            title={`${item_type_id?.toLocaleUpperCase()} specific properties`}
            icon={itemIcon}
            pretty>
            {getItemSpecificForm()}
          </Form>
        </div>
      </div>
    </div>
  );
};

/**
 * Get all the form options for the Item form
 */
const useOptions = (item_type_id: Item['item_type_id']) => {
  const { data: elements } = useAdminGetAllElements();
  const { data: itemTypes } = useGetItemTypesAll();
  const { data: rarity } = useGetRarityTypesAll();

  const elementsOptions = elements.map(element => ({
    value: element.element_id,
    label: <img className={s.element} src={addCdnUrl(element.image)} />,
  }));

  const itemTypesOptions = itemTypes.map(itemType => ({
    value: itemType.item_type_id,
    label: itemType.name,
  }));

  const rarityOptions = rarity.map(rarity => ({
    value: rarity.rarity_id,
    label: rarity.name,
  }));

  const itemIcon = (itemTypes.find(
    itemType => itemType['item_type_id'] === item_type_id,
  )?.icon ?? 'Unknown') as IconType;

  return { rarityOptions, itemTypesOptions, elementsOptions, itemIcon };
};

/**
 * Hook for handling creating an item
 */
const useCreate = () => {
  const { data, mutate, error, isSuccess } = useAdminCreateItem();

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Item created :3`);
    }
  }, [isSuccess]);

  return { data, mutate };
};

/**
 * Hook for handling updating the item
 */
const useUpdate = (item_id: string) => {
  const { mutate, error, isSuccess } = useAdminUpdateItem(item_id);

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Item updated :3`);
    }
  }, [isSuccess]);

  return { mutate };
};
